import { useCallback, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { api, setUnauthorizedHandler } from './api';
import { Layout } from './components/Layout';
import { SaveStatusProvider } from './components/ui';
import { LoginPage } from './pages/LoginPage';
import { SetupPage } from './pages/SetupPage';
import { ResetPage } from './pages/ResetPage';
import { CasesPage } from './pages/CasesPage';
import { CasePage } from './pages/CasePage';
import { AdminUsersPage } from './pages/AdminUsersPage';
import { AuditPage } from './pages/AuditPage';
import { AccountPage } from './pages/AccountPage';
import { GlossaryPage } from './pages/GlossaryPage';
import { InfoPage } from './pages/InfoPage';
import { ImpressumPage, DatenschutzPage, NutzungsbedingungenPage } from './pages/LegalPages';
import { t } from './i18n';

export interface Me {
  userId: string;
  email: string;
  displayName: string;
  role: 'ADMIN' | 'GUTACHTER' | 'MITARBEITER' | 'LESER';
  totpEnabled: boolean;
  organization: {
    id: string;
    name: string;
    licenseType: string;
    licenseExpired: boolean;
    isDemo: boolean;
    emailDomain?: string | null;
  };
}

export default function App() {
  const [me, setMe] = useState<Me | null | 'laden'>('laden');
  const navigate = useNavigate();
  const location = useLocation();

  const reloadMe = useCallback(async () => {
    try {
      const data = await api.get<Me>('/api/v1/auth/me');
      setMe(data);
      return data;
    } catch {
      setMe(null);
      return null;
    }
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setMe(null);
    });
    void reloadMe();
  }, [reloadMe]);

  useEffect(() => {
    if (me === null) {
      const publicPaths = ['/anmelden', '/einrichtung', '/passwort-zuruecksetzen', '/impressum', '/nutzungsbedingungen', '/datenschutz'];
      if (!publicPaths.includes(location.pathname)) {
        navigate('/anmelden', { replace: true });
      }
    }
  }, [me, location.pathname, navigate]);

  if (me === 'laden') {
    return <p style={{ padding: '2rem' }}>{t('app.loading')}</p>;
  }

  return (
    <SaveStatusProvider>
      <Routes>
        <Route path="/anmelden" element={<LoginPage onLoggedIn={reloadMe} />} />
        <Route path="/einrichtung" element={<SetupPage />} />
        <Route path="/passwort-zuruecksetzen" element={<ResetPage />} />
        {/* Rechtstexte öffentlich erreichbar (auch ohne Anmeldung) */}
        <Route path="/impressum" element={<ImpressumPage />} />
        <Route path="/nutzungsbedingungen" element={<NutzungsbedingungenPage />} />
        <Route path="/datenschutz" element={<DatenschutzPage />} />
        {me && (
          <Route element={<Layout me={me} onLogout={() => setMe(null)} />}>
            <Route path="/" element={<CasesPage me={me} />} />
            <Route path="/faelle/:caseId" element={<Navigate to="schritt/1" replace />} />
            <Route path="/faelle/:caseId/schritt/:step" element={<CasePage me={me} />} />
            <Route path="/faelle/:caseId/audit" element={<AuditPage />} />
            <Route path="/admin/benutzer" element={<AdminUsersPage me={me} />} />
            <Route path="/konto" element={<AccountPage me={me} onChanged={reloadMe} />} />
            <Route path="/glossar" element={<GlossaryPage />} />
            <Route path="/info" element={<InfoPage />} />
          </Route>
        )}
        <Route path="*" element={me ? <Navigate to="/" replace /> : null} />
      </Routes>
    </SaveStatusProvider>
  );
}
